/**
 * HTML canvas — Gemini-style side-by-side pane for `kind: "html"` artifacts.
 *
 * Renders the artifact body inside a sandboxed iframe so the model's HTML
 * is fully isolated from the host app (no same-origin access to host
 * cookies, storage, or parent DOM regardless of what scripts it runs).
 *
 * Two rendering strategies, switched on streaming state:
 *  - **While streaming** ({@link HtmlPreviewFrame}): one stable iframe,
 *    body-patched in place via morphdom so token-by-token updates don't
 *    flicker or re-fetch Tailwind. Scripts never execute here — a DOM-patch
 *    limitation, not a sandbox one — which is fine mid-stream since the
 *    content isn't finished anyway.
 *  - **Once complete** ({@link HtmlStaticFrame}): a genuine `srcDoc` load of
 *    the finished HTML, so any scripts the model included (a chart library,
 *    say) actually run, same as a normal page load.
 *
 * Update streams use buffered swap: while `pendingContent` is non-null the
 * iframe keeps showing the old body and a "Updating…" pill is visible. The
 * finish event swaps `pendingContent` into `content` atomically so the
 * canvas never displays half-rendered HTML.
 *
 * Power users can toggle a CodeMirror source view to inspect or hand-edit
 * the HTML; debounced auto-save persists changes through the same
 * /api/artifacts/:id PUT endpoint as document edits.
 *
 * @module components/artifacts/html-canvas
 */

import CodeMirror from "@uiw/react-codemirror";
import { langs } from "@uiw/codemirror-extensions-langs";
import { vscodeLight } from "@uiw/codemirror-theme-vscode";
import { motion } from "framer-motion";
import { Check, Code2, Copy, Download, FileText, Loader2, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { downloadArtifactPdf } from "@/lib/artifacts/download";
import { useArtifactStore } from "@/store/artifact-store";
import { api } from "@/utils/api-client";
import { ArtifactErrorBanner } from "./artifact-error-banner";
import { FocusModeToggle } from "./focus-mode-toggle";
import { GenerationSkeleton } from "./generation-skeleton";
import { HtmlPreviewFrame } from "./html-preview-frame";
import { HtmlStaticFrame } from "./html-static-frame";

/** Shape of the response from `GET /api/artifacts/:id` we read here. */
interface FetchedArtifact {
  id: string;
  title: string;
  content: string;
  updatedAt?: string;
}

/** Auto-save debounce window — matches the document canvas for consistency. */
const AUTO_SAVE_DEBOUNCE_MS = 5000;

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Side-by-side HTML artifact canvas. Mounts as a sibling of the chat root
 * inside chat.tsx via the kind dispatcher in `artifact.tsx`. Visibility is
 * driven by the artifact store so any tool call or inline preview can open
 * the canvas without prop drilling.
 */
export function HtmlCanvas() {
  const artifact = useArtifactStore((s) => s.artifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [fetchedAt, setFetchedAt] = useState<string | undefined>(undefined);
  /**
   * Whether the iframe's bootstrap shell (Tailwind + morphdom) is loaded and
   * has applied at least the first body morph. Used together with content
   * presence to decide when to lift the loading skeleton — if we lifted it
   * the instant the first stream byte landed, the user would see a flash of
   * empty white iframe while Tailwind was still fetching.
   */
  const [iframeReady, setIframeReady] = useState(false);
  /**
   * Tracks the in-flight PDF download so the toolbar button can swap to a
   * spinner. Rendering the dashboard server-side, converting to PDF, and
   * streaming it back takes several seconds — without feedback users click
   * the button repeatedly thinking nothing happened.
   */
  const [isDownloading, setIsDownloading] = useState(false);
  /**
   * Brief "copied" state for the source-view copy button — swaps the icon
   * to a check for ~1.5s after a successful clipboard write so the user
   * gets immediate, silent feedback (no toast needed).
   */
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  /**
   * View toggle. The default is `preview` so users see the rendered page
   * (including while it's still streaming). They can flip to `source` if
   * they want to inspect the markup; we don't fight that choice across
   * streams.
   */
  const [viewMode, setViewMode] = useState<"preview" | "source">("preview");
  /**
   * Counter passed to `HtmlPreviewFrame` — incrementing it forces a hard
   * iframe re-mount + bootstrap so the user can see a clean re-render of
   * the latest content (useful when their HTML contains stateful
   * elements that morphdom can't fully reset).
   */
  const [reloadKey, setReloadKey] = useState(0);

  const pendingSaveRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = useRef<string>(artifact.content);

  const isStreaming = artifact.status === "streaming";
  const isUpdateInFlight = artifact.pendingContent !== null;

  /**
   * The HTML the iframe renders.
   *
   * - **Create**: deltas land in `artifact.content` directly — `visibleHtml`
   *   tracks it and the iframe morphs in real time behind the skeleton
   *   overlay (lifts on first-paint).
   * - **Update**: deltas land in `artifact.pendingContent` (so the old body
   *   stays available in `content` for the post-finish diff). We point the
   *   iframe at `pendingContent` during the stream so the user sees the new
   *   body building up live — same pattern as a fresh create — instead of
   *   staring at a static "Updating…" overlay. When `finishStream` swaps
   *   pendingContent → content, `isUpdateInFlight` flips false and
   *   `visibleHtml` resolves to the same string from `content`, so the
   *   iframe is uninterrupted.
   */
  const visibleHtml = isUpdateInFlight
    ? (artifact.pendingContent ?? "")
    : artifact.content;

  /**
   * Persists the latest pending edit. Shared by the debounced auto-save
   * and the close-cleanup so an in-flight edit isn't lost when the user
   * closes the canvas before the debounce window elapses.
   */
  const flushSave = useCallback(async () => {
    const id = useArtifactStore.getState().artifact.id;
    const title = useArtifactStore.getState().artifact.title;
    const next = pendingSaveRef.current;
    if (!id || next === null) return;
    pendingSaveRef.current = null;
    setSaveStatus("saving");
    try {
      const response = await api.put(`/artifacts/${id}`, { title, content: next });
      const updated = (response as { data?: FetchedArtifact })?.data;
      if (updated) {
        setArtifact({ content: updated.content, title: updated.title });
        setFetchedAt(updated.updatedAt);
      }
      setSaveStatus("saved");
    } catch (err) {
      setSaveStatus("error");
      toast.error(err instanceof Error ? err.message : "Failed to save artifact");
    }
  }, [setArtifact]);

  /** Debounced edit handler used by the source-view CodeMirror onChange. */
  const handleSourceChange = useCallback(
    (markup: string) => {
      lastEmittedRef.current = markup;
      const status = useArtifactStore.getState().artifact.status;
      if (status === "streaming") return;
      if (markup === useArtifactStore.getState().artifact.content) return;
      // Push to the store immediately so toggling back to preview reflects
      // edits without waiting for the auto-save round-trip.
      setArtifact({ content: markup });
      pendingSaveRef.current = markup;
      setSaveStatus("saving");
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void flushSave();
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [flushSave, setArtifact],
  );

  /**
   * Fetch the canonical artifact body when the canvas opens or the id
   * changes. Streamed deltas reach the store first, but a fresh open after
   * a page reload needs the persisted body since the data stream is gone.
   */
  useEffect(() => {
    if (!artifact.isVisible || !artifact.id || artifact.status === "streaming") return;
    let cancelled = false;
    const fetchId = artifact.id;
    api
      .get(`/artifacts/${fetchId}`)
      .then((response) => {
        if (cancelled) return;
        // Skip a late response for an artifact the user switched away from.
        if (useArtifactStore.getState().artifact.id !== fetchId) return;
        const fetched = (response as { data?: FetchedArtifact })?.data;
        if (!fetched) return;
        setFetchedAt(fetched.updatedAt);
        if (fetched.content && fetched.content !== artifact.content) {
          setArtifact({ content: fetched.content, title: fetched.title });
        }
      })
      .catch(() => {
        // Silent — the store may already have streamed content; if not,
        // the canvas just shows what's there.
      });
    return () => {
      cancelled = true;
    };
  }, [artifact.isVisible, artifact.id, artifact.status, artifact.content, setArtifact]);

  /**
   * Reset the iframe-ready flag whenever the artifact id changes, the user
   * forces a refresh, or streaming starts/stops. The first two re-mount the
   * live iframe via its composed React key, so a fresh bootstrap handshake
   * is required before the next paint. The streaming transition swaps
   * between two entirely different components ({@link HtmlPreviewFrame} ↔
   * {@link HtmlStaticFrame}) at the same JSX position — React mounts a new
   * one, so without this it would keep showing "ready" from the outgoing
   * component while the incoming one is still loading.
   */
  useEffect(() => {
    setIframeReady(false);
  }, [artifact.id, reloadKey, isStreaming]);

  /**
   * When an update stream starts, treat it like a fresh create: re-mount the
   * iframe with an empty body (so morphdom doesn't try to diff old content
   * into half-written new content), drop iframeReady so the skeleton
   * overlay reappears, and let the new body stream in. The reloadKey bump
   * cascades into the effect above which also resets `iframeReady`.
   */
  useEffect(() => {
    if (!isUpdateInFlight) return;
    setReloadKey((k) => k + 1);
  }, [isUpdateInFlight]);

  /**
   * Flush any pending edit and clear the timer when the canvas closes or
   * unmounts so users never lose work to a half-elapsed debounce window.
   */
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (pendingSaveRef.current !== null) {
        // Fire-and-forget; the promise outlives the React lifecycle.
        void flushSave();
      }
    };
  }, [flushSave]);

  async function handleCopySource() {
    try {
      await navigator.clipboard.writeText(visibleHtml);
      setCopiedAt(Date.now());
      window.setTimeout(() => setCopiedAt(null), 1500);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't copy to clipboard");
    }
  }

  async function handleDownload() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadArtifactPdf({ id: artifact.id, title: artifact.title });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 80, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex h-full min-w-0 flex-[3] flex-col border-l bg-background shadow-2xl max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex-none max-lg:border-l-0"
    >
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold leading-tight">
            {artifact.title || "Untitled"}
          </h2>
          <SubtitleRow
            fetchedAt={fetchedAt}
            isStreaming={isStreaming}
            isUpdateInFlight={isUpdateInFlight}
            saveStatus={saveStatus}
          />
        </div>

        {/**
         * Refresh — only visible in preview mode. Forces a hard iframe
         * re-mount via `reloadKey` so the latest content re-renders from
         * a fresh bootstrap. Useful when model output contains scripts
         * or stateful elements that morphdom can't fully reset.
         */}
        {viewMode === "preview" ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={isStreaming}
            title="Refresh preview"
          >
            <RefreshCw className="size-5" />
          </Button>
        ) : null}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setViewMode((m) => (m === "preview" ? "source" : "preview"))}
          title={viewMode === "preview" ? "View HTML source" : "View rendered"}
          aria-pressed={viewMode === "source"}
        >
          {viewMode === "preview" ? (
            <Code2 className="size-5" />
          ) : (
            <FileText className="size-5" />
          )}
        </Button>

        {/**
         * Copy source — only visible in source mode. Writes the current HTML
         * body to the clipboard so developers can paste it into their own
         * tooling. Check-mark feedback lasts ~1.5s and avoids a toast for
         * this high-frequency action.
         */}
        {viewMode === "source" ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleCopySource}
            disabled={isStreaming || visibleHtml.length === 0}
            title={copiedAt ? "Copied!" : "Copy HTML"}
            aria-label="Copy HTML"
          >
            {copiedAt ? (
              <Check className="size-5 text-green-600" />
            ) : (
              <Copy className="size-5" />
            )}
          </Button>
        ) : null}

        <FocusModeToggle />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          disabled={isDownloading}
          title={isDownloading ? "Preparing PDF…" : "Download PDF"}
        >
          {isDownloading ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Download className="size-5" />
          )}
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={hideArtifact} title="Close">
          <X className="size-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-hidden bg-muted/20">
        {viewMode === "source" ? (
          <div className="h-full overflow-auto bg-white">
            <CodeMirror
              value={visibleHtml}
              extensions={[langs.html()]}
              theme={vscodeLight}
              editable={!isStreaming}
              onChange={handleSourceChange}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: !isStreaming,
              }}
              className="text-sm"
            />
          </div>
        ) : (
          /**
           * Iframe is mounted as soon as we have an artifact id so Tailwind
           * (and, while streaming, morphdom) start fetching in the
           * background. The skeleton sits on top of it (absolutely
           * positioned) until the content is ready to show — otherwise the
           * user sees an empty white iframe between "first byte" and
           * "iframe paints first content".
           *
           * `isStreaming` picks which iframe implementation is mounted, not
           * just a display flag — see the module doc for why the two exist.
           */
          <div className="relative h-full w-full">
            {artifact.id ? (
              isStreaming ? (
                <HtmlPreviewFrame
                  artifactId={artifact.id}
                  html={visibleHtml}
                  title={artifact.title}
                  reloadKey={reloadKey}
                  onReady={() => setIframeReady(true)}
                />
              ) : (
                <HtmlStaticFrame
                  html={visibleHtml}
                  title={artifact.title}
                  reloadKey={reloadKey}
                  onReady={() => setIframeReady(true)}
                />
              )
            ) : null}
            {visibleHtml.length === 0 || !iframeReady ? (
              <div className="absolute inset-0">
                <GenerationSkeleton kind="html" title={artifact.title} />
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/*
        * Update streams reuse the same skeleton + streaming-iframe path as
        * creates (see `visibleHtml` and the `isUpdateInFlight` effect above),
        * so we don't mount the full-screen `ArtifactUpdateOverlay` here. The
        * error/retry banner stays — it shows only on `status === "error"`.
        */}
      <ArtifactErrorBanner />
    </motion.div>
  );
}

/**
 * Subtitle row beneath the canvas title. Mirrors the document canvas's
 * subtitle so the two surfaces feel consistent.
 */
function SubtitleRow({
  fetchedAt,
  isStreaming,
  isUpdateInFlight,
  saveStatus,
}: {
  fetchedAt: string | undefined;
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  saveStatus: SaveStatus;
}) {
  const timeLabel = fetchedAt ? updatedLabel(fetchedAt) : null;
  const statusLabel = activeStatusLabel({ isStreaming, isUpdateInFlight, saveStatus });

  if (!timeLabel && !statusLabel)
    return <span className="text-xs text-muted-foreground">&nbsp;</span>;

  return (
    <span className="text-xs text-muted-foreground">
      {timeLabel}
      {timeLabel && statusLabel ? <span className="mx-1.5 opacity-50">|</span> : null}
      {statusLabel}
    </span>
  );
}

function activeStatusLabel({
  isStreaming,
  isUpdateInFlight,
  saveStatus,
}: {
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  saveStatus: SaveStatus;
}): string | null {
  if (isUpdateInFlight) return "Updating…";
  if (isStreaming) return "Generating…";
  if (saveStatus === "saving") return "Saving…";
  if (saveStatus === "saved") return "Saved";
  if (saveStatus === "error") return "Save failed";
  return null;
}

function updatedLabel(timestamp?: string): string {
  if (!timestamp) return "Recently updated";
  const updated = new Date(timestamp);
  if (Number.isNaN(updated.getTime())) return "Recently updated";
  const diffMs = Date.now() - updated.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return updated.toLocaleDateString();
}
